import {
    autoUpdate,
    flip,
    FloatingFocusManager,
    FloatingList,
    FloatingNode,
    FloatingPortal,
    FloatingTree,
    offset,
    safePolygon,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useFloatingParentNodeId,
    useFloatingTree,
    useHover,
    useInteractions,
    useListItem,
    useListNavigation,
    useMergeRefs,
    useRole,
    useTypeahead
} from '@floating-ui/react';
import { SvgIcon } from '@progress/kendo-react-common';
import { SVGIcon } from '@progress/kendo-svg-icons';
import clsx from 'clsx';
import * as React from 'react';
import { useLocation } from 'react-router';
import { useMultiWindowContext } from '../../../app/components/multi-window/context/multi-window-context';
import { RoutePath } from '../../../app/router/routes';
import { ChevronRightOutline } from '../../../icons/icons';
import './DropdownMenu.css';

const MenuContext = React.createContext<{
    getItemProps: (userProps?: React.HTMLProps<HTMLElement>) => Record<string, unknown>;
    activeIndex: number | null;
    setActiveIndex: React.Dispatch<React.SetStateAction<number | null>>;
    setHasFocusInside: React.Dispatch<React.SetStateAction<boolean>>;
    isOpen: boolean;
    closeOnClick?: boolean;
    size?: 'small' | 'medium' | 'large';
}>({
    getItemProps: () => ({}),
    activeIndex: null,
    setActiveIndex: () => {},
    setHasFocusInside: () => {},
    isOpen: false
});

interface MenuProps {
    customContent?: React.ReactElement<any>;
    label?: string;
    icon?: SVGIcon;
    children?: React.ReactNode;
    closeOnClick?: boolean;
    onFocusInOrMouseLeave?: () => void;
    onFocusOutOrMouseOut?: () => void;
    menuSize?: 'small' | 'medium' | 'large';
}

// size styles map
const SIZE_STYLES = {
    small: { fontSize: 12, padding: 'py-1 px-2', minWidth: 100 },
    medium: { fontSize: 14, padding: 'py-2 px-3', minWidth: 150 },
    large: { fontSize: 16, padding: 'py-3 px-4', minWidth: 200 }
} as const;

export const MenuComponent = React.forwardRef<HTMLElement, MenuProps & React.HTMLProps<HTMLElement>>(
    ({ children, label, customContent, icon, menuSize = 'small', ...props }, forwardedRef) => {
        const multiWindowContext = useMultiWindowContext();
        const [isOpen, setIsOpen] = React.useState(false);
        const [hasFocusInside, setHasFocusInside] = React.useState(false);
        const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
        const [hasHover, setHasHover] = React.useState(false);

        const elementsRef = React.useRef<Array<HTMLElement | null>>([]);
        const labelsRef = React.useRef<Array<string | null>>([]);
        const parent = React.useContext(MenuContext);

        const tree = useFloatingTree();
        const nodeId = useFloatingNodeId();
        const parentId = useFloatingParentNodeId();
        const item = useListItem();
        const isNested = parentId !== null;

        // determine effective size (inherit when nested)
        const effectiveSize: 'small' | 'medium' | 'large' = isNested ? (parent.size ?? 'small') : menuSize;
        const sizeStyles = SIZE_STYLES[effectiveSize];

        const { floatingStyles, refs, context } = useFloating<HTMLElement>({
            nodeId,
            open: isOpen,
            onOpenChange: setIsOpen,
            placement: isNested ? 'right-start' : 'bottom-start',
            middleware: [offset({ mainAxis: 1, alignmentAxis: 0 }), flip(), shift()],
            whileElementsMounted: autoUpdate
        });

        const hover = useHover(context, {
            enabled: isNested && !props.disabled,
            delay: { open: 75 },
            handleClose: safePolygon({ blockPointerEvents: true, buffer: 10 })
        });
        const click = useClick(context, {
            enabled: !props.disabled,
            event: 'mousedown',
            toggle: !isNested,
            ignoreMouse: isNested
        });
        const role = useRole(context, { role: 'menu' });
        const dismiss = useDismiss(context, { bubbles: true });
        const listNavigation = useListNavigation(context, {
            enabled: !props.disabled,
            listRef: elementsRef,
            activeIndex,
            nested: isNested,
            onNavigate: (ai: number | null) => {
                setActiveIndex(ai);
                // Fixes stuck focus inside
                if (!isOpen) {
                    if (ai === null) {
                        setHasFocusInside(false);
                    }
                }
            }
        });
        const typeahead = useTypeahead(context, {
            enabled: !props.disabled,
            listRef: labelsRef,
            onMatch: isOpen ? setActiveIndex : undefined,
            activeIndex
        });

        const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
            hover,
            click,
            role,
            dismiss,
            listNavigation,
            typeahead
        ]);

        // Event emitter allows you to communicate across tree components.
        // This effect closes all menus when an item gets clicked anywhere
        // in the tree.
        React.useEffect(() => {
            if (!tree) {
                return;
            }

            function handleTreeClick() {
                setIsOpen(false);
            }

            function onSubMenuOpen(event: { nodeId: string; parentId: string }) {
                if (event.nodeId !== nodeId && event.parentId === parentId) {
                    setIsOpen(false);
                }
            }

            tree.events.on('click', handleTreeClick);
            tree.events.on('menuopen', onSubMenuOpen);

            return () => {
                tree.events.off('click', handleTreeClick);
                tree.events.off('menuopen', onSubMenuOpen);
            };
        }, [tree, nodeId, parentId]);

        React.useEffect(() => {
            if (isOpen && tree) {
                tree.events.emit('menuopen', { parentId, nodeId });
            }
        }, [tree, isOpen, nodeId, parentId]);

        React.useEffect(() => {
            if (hasFocusInside || hasHover) {
                props.onFocusInOrMouseLeave?.();
            } else {
                props.onFocusOutOrMouseOut?.();
            }
        }, [hasHover, isOpen, hasFocusInside]);

        if (isNested) {
            return (
                <FloatingNode id={nodeId}>
                    {customContent ? (
                        React.cloneElement(customContent, {
                            ref: useMergeRefs([refs.setReference, item.ref, forwardedRef]),
                            tabIndex: !isNested ? undefined : parent.activeIndex === item.index ? 0 : -1,
                            role: isNested ? 'menuitem' : undefined,
                            ['data-open']: isOpen ? '' : undefined,
                            ['data-nested']: isNested ? '' : undefined,
                            ['data-focus-inside']: hasFocusInside ? '' : undefined,
                            className: 'dropdown-item ' + (customContent.props?.className ?? ''),
                            ...getReferenceProps(
                                parent.getItemProps({
                                    ...props,
                                    onPointerEnter(event: React.PointerEvent<HTMLElement>) {
                                        props.onPointerEnter?.(event);
                                        setHasHover(true);
                                    },
                                    onPointerLeave(event: React.PointerEvent<HTMLElement>) {
                                        props.onPointerLeave?.(event);
                                        setHasHover(false);
                                    },
                                    onFocus(event: React.FocusEvent<HTMLElement>) {
                                        props.onFocus?.(event);
                                        setHasFocusInside(false);
                                        parent.setHasFocusInside(true);
                                    }
                                })
                            )
                        })
                    ) : (
                        <button
                            ref={useMergeRefs([refs.setReference, item.ref, forwardedRef])}
                            tabIndex={!isNested ? undefined : parent.activeIndex === item.index ? 0 : -1}
                            role={isNested ? 'menuitem' : undefined}
                            data-open={isOpen ? '' : undefined}
                            data-nested={isNested ? '' : undefined}
                            data-focus-inside={hasFocusInside ? '' : undefined}
                            {...getReferenceProps(
                                parent.getItemProps({
                                    ...props,
                                    onPointerEnter(event: React.PointerEvent<HTMLElement>) {
                                        props.onPointerEnter?.(event);
                                        setHasHover(true);
                                    },
                                    onPointerLeave(event: React.PointerEvent<HTMLElement>) {
                                        props.onPointerLeave?.(event);
                                        setHasHover(false);
                                    },
                                    onFocus(event: React.FocusEvent<HTMLElement>) {
                                        props.onFocus?.(event);
                                        setHasFocusInside(false);
                                        parent.setHasFocusInside(true);
                                    }
                                })
                            )}
                            style={{
                                fontSize: sizeStyles.fontSize,
                                cursor: 'pointer',
                                ...props.style
                            }}
                            className={clsx('dropdown-item', sizeStyles.padding, props.className)}>
                            <div className="d-flex align-items-center gap-2">
                                <SvgIcon icon={icon} size="small" className="flex-shrink-0" />
                                <span className="flex-grow-1 text-start">{label}</span>
                                {isNested && <SvgIcon icon={ChevronRightOutline} size="small" className="flex-shrink-0" />}
                            </div>
                        </button>
                    )}
                    <MenuContext.Provider
                        value={{
                            activeIndex,
                            setActiveIndex,
                            getItemProps,
                            setHasFocusInside,
                            isOpen,
                            closeOnClick: props.closeOnClick,
                            size: effectiveSize
                        }}>
                        <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
                            {isOpen && (
                                <FloatingPortal root={multiWindowContext.root}>
                                    <FloatingFocusManager
                                        context={context}
                                        modal={false}
                                        initialFocus={isNested ? -1 : 0}
                                        returnFocus={!isNested}>
                                        <div
                                            ref={refs.setFloating}
                                            className="k-card d-flex flex-column z-2"
                                            style={{ ...floatingStyles, minWidth: sizeStyles.minWidth, fontSize: sizeStyles.fontSize }}
                                            {...getFloatingProps()}>
                                            {children}
                                        </div>
                                    </FloatingFocusManager>
                                </FloatingPortal>
                            )}
                        </FloatingList>
                    </MenuContext.Provider>
                </FloatingNode>
            );
        }

        return (
            <FloatingNode id={nodeId}>
                <MenuContext.Provider
                    value={{
                        activeIndex,
                        setActiveIndex,
                        getItemProps,
                        setHasFocusInside,
                        isOpen,
                        size: effectiveSize
                    }}>
                    <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
                        <FloatingFocusManager context={context} modal={false} initialFocus={isNested ? -1 : 0} returnFocus={!isNested}>
                            <div
                                ref={refs.setFloating}
                                className="dropdown-item d-flex flex-column z-2"
                                style={{ minWidth: sizeStyles.minWidth, fontSize: sizeStyles.fontSize }}
                                {...getFloatingProps()}>
                                {children}
                            </div>
                        </FloatingFocusManager>
                    </FloatingList>
                </MenuContext.Provider>
            </FloatingNode>
        );
    }
);

interface MenuItemProps {
    label?: string;
    labelElement?: React.ReactNode;
    icon?: SVGIcon;
    suffixIcon?: SVGIcon;
    suffixElement?: React.ReactNode;
    disabled?: boolean;
    hint?: string | null;
    children?: React.ReactNode;
}

export const MenuItem = React.forwardRef<HTMLElement, MenuItemProps & React.ButtonHTMLAttributes<HTMLElement>>(
    ({ label, labelElement, disabled, icon, suffixIcon, suffixElement, hint, children, ...props }, forwardedRef) => {
        const menu = React.useContext(MenuContext);
        const item = useListItem({ label: disabled ? null : label });
        const tree = useFloatingTree();
        const isActive = item.index === menu.activeIndex;
        const sizeStyles = SIZE_STYLES[menu.size ?? 'small'];
        if (children) {
            return <div>{children}</div>;
        }
        return (
            <button
                {...props}
                ref={useMergeRefs([item.ref, forwardedRef])}
                type="button"
                role="menuitem"
                className={`dropdown-item d-flex align-items-center gap-2 ${sizeStyles.padding} ${props?.className}`}
                tabIndex={isActive ? 0 : -1}
                disabled={disabled}
                style={{
                    fontSize: sizeStyles.fontSize,
                    cursor: 'pointer'
                }}
                {...menu.getItemProps({
                    onClick(event: React.MouseEvent<HTMLElement>) {
                        props.onClick?.(event);
                        if (menu.closeOnClick ?? true) {
                            tree?.events.emit('click');
                        }
                    },
                    onFocus(event: React.FocusEvent<HTMLElement>) {
                        props.onFocus?.(event);
                        menu.setHasFocusInside(true);
                    }
                })}>
                <SvgIcon icon={icon} />
                <div className={'w-100 d-flex justify-content-between align-items-center'}>
                    {hint ? (
                        <div className="d-flex flex-column align-items-start text-start">
                            <span>{label || labelElement}</span>
                            <span className="secondary-text" style={{ fontSize: 9, whiteSpace: 'normal', maxWidth: 260 }}>
                                {hint}
                            </span>
                        </div>
                    ) : (
                        label || labelElement
                    )}
                    <SvgIcon icon={suffixIcon} />
                    {suffixElement && <div className="ms-2">{suffixElement}</div>}
                </div>
            </button>
        );
    }
);

const pathToTagMap: Partial<Record<RoutePath, { text: string; color: string }>> = {
    '/': { text: 'Home', color: 'light-dark(var(--color-neutral-700), var(--color-neutral-500))' }
};
export const NavigatingMenuItem = React.forwardRef<
    HTMLElement,
    MenuItemProps & React.ButtonHTMLAttributes<HTMLElement> & { navigateTo: RoutePath }
>(({ label, disabled, icon, ...props }, forwardedRef) => {
    const location = useLocation();
    const tagInfo = pathToTagMap[props.navigateTo];
    const shouldShowSuffix = location.pathname !== props.navigateTo && tagInfo != null;
    return (
        <MenuItem
            ref={forwardedRef}
            label={label}
            disabled={disabled}
            icon={icon}
            suffixElement={
                shouldShowSuffix ? (
                    <div className={'fe-badge-company'} style={{ backgroundColor: tagInfo.color }}>
                        {tagInfo.text}
                    </div>
                ) : undefined
            }
            {...props}
        />
    );
});

export const Menu = React.forwardRef<HTMLElement, MenuProps & React.HTMLProps<HTMLElement>>((props, ref) => {
    const parentId = useFloatingParentNodeId();
    if (parentId === null) {
        return (
            <FloatingTree>
                <MenuComponent {...props} ref={ref} />
            </FloatingTree>
        );
    }

    return <MenuComponent {...props} ref={ref} />;
});

// TODO: make a composite menu item so that multiple menu items don't have to be wrapped in an empty <Menu/> element
